import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { PHONE, PHONE_TEL, EMAIL, LOCATION, AREA } from "@/lib/site-data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Terrassement",
  "Creusement de tranchées",
  "Création de cour",
  "Aménagement extérieur",
  "Allée / Accès",
  "Rénovation",
  "Autre",
];

export const ContactSection = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    project_type: PROJECT_TYPES[0],
    message: "",
    website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      if (data?.email_sent) {
        toast.success("Demande envoyée ! Nous vous recontactons sous 24h.");
      } else {
        toast.success("Demande enregistrée. Nous vous rappelons rapidement.");
      }
      setForm({ name: "", phone: "", email: "", project_type: PROJECT_TYPES[0], message: "", website: "" });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler directement.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 asphalt-bg border-t border-bgtp-line">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
        {/* Left — info */}
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">Contact & Devis</div>
          <h2 className="font-heading text-5xl md:text-7xl uppercase tracking-tight leading-[0.9] text-white">
            Parlons de <br />
            <span className="brush-highlight"><span className="relative z-10 text-white">votre chantier.</span></span>
          </h2>
          <p className="mt-6 text-white/75 leading-relaxed max-w-md">
            Décrivez votre projet — terrassement, aménagement extérieur ou rénovation —
            nous revenons vers vous avec un devis clair sous 48h.
          </p>

          <div className="mt-10 space-y-5">
            <a
              data-testid="contact-info-phone"
              href={`tel:${PHONE_TEL}`}
              className="flex items-center gap-4 border-l-2 border-bgtp-orange pl-5 py-2 hover:bg-bgtp-ink transition-colors"
            >
              <Phone className="w-6 h-6 text-bgtp-orange" />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-bgtp-steel">Téléphone</div>
                <div className="font-heading text-2xl tracking-wider text-white">{PHONE}</div>
              </div>
            </a>
            <a
              data-testid="contact-info-email"
              href={`mailto:${EMAIL}`}
              className="flex items-center gap-4 border-l-2 border-bgtp-orange pl-5 py-2 hover:bg-bgtp-ink transition-colors"
            >
              <Mail className="w-6 h-6 text-bgtp-orange" />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-bgtp-steel">Email</div>
                <div className="text-lg text-white break-all">{EMAIL}</div>
              </div>
            </a>
            <div data-testid="contact-info-location" className="flex items-center gap-4 border-l-2 border-bgtp-orange pl-5 py-2">
              <MapPin className="w-6 h-6 text-bgtp-orange" />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-bgtp-steel">Zone d'intervention</div>
                <div className="text-lg text-white">{LOCATION}</div>
                <div className="text-xs text-bgtp-steel">{AREA}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-7">
          <div className="relative">
            <div className="absolute -inset-2 border border-bgtp-orange/30 pointer-events-none" />
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="relative bg-bgtp-ink border border-bgtp-line p-7 md:p-10 space-y-6"
            >
              <div className="flex items-center justify-between border-b border-bgtp-line pb-4">
                <div className="font-heading text-2xl uppercase tracking-wider text-white">Demande de devis</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-bgtp-orange">Gratuit · 48h</div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="label-eyebrow" htmlFor="name">Nom complet *</label>
                  <input
                    id="name"
                    data-testid="contact-input-name"
                    className="input-line"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
                <div>
                  <label className="label-eyebrow" htmlFor="phone">Téléphone *</label>
                  <input
                    id="phone"
                    data-testid="contact-input-phone"
                    className="input-line"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="07 12 34 56 78"
                    required
                  />
                </div>
                <div>
                  <label className="label-eyebrow" htmlFor="email">Email *</label>
                  <input
                    id="email"
                    data-testid="contact-input-email"
                    className="input-line"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="vous@exemple.fr"
                    required
                  />
                </div>
                <div>
                  <label className="label-eyebrow" htmlFor="ptype">Type de projet *</label>
                  <select
                    id="ptype"
                    data-testid="contact-input-project-type"
                    className="input-line appearance-none"
                    value={form.project_type}
                    onChange={update("project_type")}
                  >
                    {PROJECT_TYPES.map((p) => (
                      <option key={p} value={p} className="bg-bgtp-ink">{p}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="label-eyebrow" htmlFor="msg">Décrivez votre projet *</label>
                <textarea
                  id="msg"
                  data-testid="contact-input-message"
                  className="input-line min-h-[120px] resize-y"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Surface, accès, délais souhaités, contraintes..."
                  required
                />
              </div>

              {/* Honeypot — must remain hidden from real users */}
              <div aria-hidden="true" className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" tabIndex={-1}>
                <label htmlFor="website">Site web (laisser vide)</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between pt-2">
                <p className="text-xs text-bgtp-steel max-w-xs">
                  En envoyant ce formulaire, vous acceptez d'être recontacté par BGTP au sujet de votre demande.
                </p>
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Envoi...
                    </>
                  ) : submitted ? (
                    <>
                      <CheckCircle2 className="w-5 h-5" /> Envoyé
                    </>
                  ) : (
                    <>
                      Envoyer la demande <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
